// client/src/coms/personalisation/ServerPrefsSettings.js
// Settings → Appearance → This server: overrides that apply only in the current server. Anything not set here
// follows the account-wide settings (and keeps following them when those change). Server admins can also
// suggest a theme for members here; members always choose whether to use it.
import React, { useMemo, useState } from 'react';
import { usePersonalisation } from './PersonalisationProvider';
import { LAYOUT_PRESET_INFO, inheritedValue } from './layoutModel';
import { InheritBadge, Row, Section } from './settingsControls';
import { ThemeEditor, ThemeSwatch } from './ThemeSettings';
import { themeAccessibility, themeFromBuiltIn } from './themeEngine';

const INHERIT = '__inherit__';

function OverrideSelect({ label, k, options, overrides, global, onSet, describe, testId }) {
  const own = Object.prototype.hasOwnProperty.call(overrides, k);
  const inherited = inheritedValue(global, k);
  const show = (v) => (describe ? describe(v) : String(v));
  return (
    <Row label={label} badge={<InheritBadge inherited={!own} />}>
      <div className="vx-inline">
        <select
          className="vx-select"
          aria-label={label}
          value={own ? String(overrides[k]) : INHERIT}
          onChange={(e) => {
            const v = e.target.value;
            if (v === INHERIT) onSet(k, undefined);
            else {
              const opt = options.find((o) => String(o.value) === v);
              onSet(k, opt ? opt.value : v);
            }
          }}
          data-testid={testId}
        >
          <option value={INHERIT}>Use my setting ({show(inherited)})</option>
          {options.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </select>
        {own ? (
          <button type="button" className="vx-btn small" onClick={() => onSet(k, undefined)} aria-label={`Reset ${label} to your account setting`}>
            Reset
          </button>
        ) : null}
      </div>
    </Row>
  );
}

const showHide = [
  { value: true, label: 'Show' },
  { value: false, label: 'Hide' },
];

export default function ServerPrefsSettings({ serverKey, serverName, channels = [] }) {
  const pz = usePersonalisation();
  const overrides = (pz.serverOverrides && pz.serverOverrides[serverKey]) || {};
  const global = pz.globalPrefs;
  const set = (k, v) => pz.setServerValue(serverKey, k, v);
  const st = pz.serverTheme && pz.serverTheme.key === serverKey ? pz.serverTheme : { theme: null, canEdit: false };
  const [editingSuggestion, setEditingSuggestion] = useState(false);
  const [suggestionError, setSuggestionError] = useState('');

  const presetLabel = (v) => (LAYOUT_PRESET_INFO.find((p) => p.id === v) || { label: v }).label;
  const textChannels = useMemo(() => channels.filter((c) => String(c.kind || 'text') === 'text'), [channels]);
  const themeOptions = [
    ...(st.theme ? [{ value: 'server', label: `This server’s theme (${st.theme.name || 'suggested'})` }] : []),
    { value: 'dark', label: 'Dark' },
    { value: 'light', label: 'Light' },
    { value: 'oled', label: 'OLED black' },
    { value: 'contrast', label: 'High contrast' },
    ...pz.themes.map((t) => ({ value: `custom:${t.id}`, label: `My theme: ${t.name}` })),
  ];
  const customCount = Object.keys(overrides).filter((k) => k !== 'theme.suggestionSeen').length;

  if (!serverKey) {
    return (
      <Section id="vx-server" title="This server" description="Open a server to give it its own layout and theme.">
        <div className="vx-set-hint" id="vx-setting-server-prefs" data-setting="server-prefs">
          No server is open. Open one, then come back here to give it its own layout, theme, landing channel and unread badges.
        </div>
      </Section>
    );
  }

  return (
    <Section
      id="vx-server"
      title={`This server${serverName ? `: ${serverName}` : ''}`}
      description="Personal preferences that apply only while you are in this server; nobody else sees them. Everything left on “Use my setting” follows your account settings. Server-wide settings for everyone are in Server Settings."
      actions={
        <button type="button" className="vx-btn small" disabled={!customCount} onClick={() => pz.resetServer(serverKey)} data-testid="server-prefs-reset">
          Reset this server
        </button>
      }
    >
      <div className="vx-set-hint" data-testid="server-prefs-count" id="vx-setting-server-prefs" data-setting="server-prefs">
        {customCount ? `${customCount} setting${customCount === 1 ? '' : 's'} customised for this server.` : 'Nothing customised yet: this server uses your account settings.'}
      </div>
      <OverrideSelect
        label="Layout preset"
        k="layout.preset"
        overrides={overrides}
        global={global}
        onSet={set}
        describe={presetLabel}
        options={LAYOUT_PRESET_INFO.filter((p) => p.id !== 'custom').map((p) => ({ value: p.id, label: p.label }))}
        testId="server-pref-preset"
      />
      <OverrideSelect
        label="Theme"
        k="theme.choice"
        overrides={overrides}
        global={global}
        onSet={set}
        describe={() => 'my theme'}
        options={themeOptions}
        testId="server-pref-theme"
      />
      <OverrideSelect label="Channel list" k="layout.sidebar.visible" overrides={overrides} global={global} onSet={set} describe={(v) => (v ? 'shown' : 'hidden')} options={showHide} testId="server-pref-sidebar" />
      <OverrideSelect label="Member list" k="layout.members.visible" overrides={overrides} global={global} onSet={set} describe={(v) => (v ? 'shown' : 'hidden')} options={showHide} testId="server-pref-members" />
      <OverrideSelect
        label="Chat density"
        k="layout.chatDensity"
        overrides={overrides}
        global={global}
        onSet={set}
        describe={(v) => (v === 'compact' ? 'compact' : 'cozy')}
        options={[
          { value: 'cozy', label: 'Cozy' },
          { value: 'compact', label: 'Compact' },
        ]}
        testId="server-pref-chat-density"
      />
      <OverrideSelect
        label="Spacing"
        k="layout.density"
        overrides={overrides}
        global={global}
        onSet={set}
        options={[
          { value: 'comfortable', label: 'Comfortable' },
          { value: 'compact', label: 'Compact' },
        ]}
      />
      <OverrideSelect
        label="Conversation tabs"
        k="layout.tabBar"
        overrides={overrides}
        global={global}
        onSet={set}
        options={[
          { value: 'auto', label: 'Automatic' },
          { value: 'always', label: 'Always' },
          { value: 'never', label: 'Never' },
        ]}
      />
      <OverrideSelect
        label="While an activity is open"
        k="layout.activityPlacement"
        overrides={overrides}
        global={global}
        onSet={set}
        describe={(v) => (v === 'beside-chat' ? 'beside the chat' : 'full width')}
        options={[
          { value: 'main', label: 'Full width' },
          { value: 'beside-chat', label: 'Beside the chat' },
        ]}
        testId="server-pref-activity"
      />
      <OverrideSelect label="Activities button" k="layout.activitiesButton" overrides={overrides} global={global} onSet={set} describe={(v) => (v ? 'shown' : 'hidden')} options={showHide} />
      <OverrideSelect label="Pinned message banner" k="layout.headerBar" overrides={overrides} global={global} onSet={set} describe={(v) => (v ? 'shown' : 'hidden')} options={showHide} />
      <OverrideSelect
        label="When I open this server"
        k="nav.landingChannel"
        overrides={overrides}
        global={global}
        onSet={(k, v) => set(k, v === '' ? undefined : v)}
        describe={() => 'last visited channel'}
        options={textChannels.map((c) => ({ value: String(c.id), label: `Go to #${c.name}` }))}
        testId="server-pref-landing"
      />
      <OverrideSelect
        label="Unread badge for this server"
        k="notifications.badges"
        overrides={overrides}
        global={global}
        onSet={set}
        describe={() => 'all unread'}
        options={[
          { value: 'all', label: 'All unread messages' },
          { value: 'mentions', label: 'Mentions only' },
          { value: 'none', label: 'No badge' },
        ]}
        testId="server-pref-badges"
      />
      <div className="vx-set-hint">Badges only change how this server is shown in your server list; notification rules are unchanged.</div>

      {st.canEdit ? (
        <div className="vx-set-subsection" data-testid="server-theme-admin">
          <h4 className="vx-set-subtitle">Suggested theme for members</h4>
          <div className="vx-set-hint">
            Members are offered this theme and decide whether to use it. It must be readable; unreadable colours are refused.
          </div>
          {suggestionError ? (
            <div className="vx-acct-error" role="alert">
              {suggestionError}
            </div>
          ) : null}
          {st.theme && !editingSuggestion ? (
            <div className="vx-inline" style={{ marginTop: 8 }}>
              <ThemeSwatch theme={st.theme} size="sm" />
              <span>{st.theme.name}</span>
              <button type="button" className="vx-btn small" onClick={() => setEditingSuggestion(true)}>
                Edit
              </button>
              <button
                type="button"
                className="vx-btn small danger"
                onClick={async () => {
                  try {
                    await pz.saveServerTheme(serverKey, null);
                  } catch (e) {
                    setSuggestionError(e.message || 'Could not remove the suggested theme.');
                  }
                }}
              >
                Remove suggestion
              </button>
            </div>
          ) : null}
          {!st.theme && !editingSuggestion ? (
            <button type="button" className="vx-btn small primary" style={{ marginTop: 8 }} onClick={() => setEditingSuggestion(true)} data-testid="server-theme-create">
              Suggest a theme
            </button>
          ) : null}
          {editingSuggestion ? (
            <ThemeEditor
              initial={st.theme || themeFromBuiltIn('dark', `${serverName || 'Server'} theme`)}
              title="Suggested theme"
              saveLabel="Save suggestion"
              onCancel={() => setEditingSuggestion(false)}
              onSave={async (theme) => {
                setSuggestionError('');
                if (!themeAccessibility(theme).ok) throw new Error('Make the text readable on every surface before suggesting this theme.');
                try {
                  await pz.saveServerTheme(serverKey, theme);
                  setEditingSuggestion(false);
                } catch (e) {
                  const failures = e && e.data && e.data.failures;
                  throw new Error(failures ? `Not readable enough: ${failures.map((f) => f.pair).join(', ')}.` : e.message);
                }
              }}
            />
          ) : null}
        </div>
      ) : null}
    </Section>
  );
}
